import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, MapPin, TrendingDown, Search } from "lucide-react";
import Header from "@/components/quote/Header";
import { NumberField } from "@/components/quote/NumberField";
import { computeQuote, DEFAULT_MODEL, usd } from "@/lib/calc";
import { fetchRates } from "@/lib/api";

const DEFAULTS = {
  storageSqft: 2000,
  storageMonths: 1,
  inboundPallets: 40,
  monthlyOrders: 800,
  linesPerOrder: 3,
  repackUnits: 150,
  outboundPallets: 35,
  gaPercent: 10,
};

function useLocationRates(query, setModel) {
  const [loc, setLoc] = useState(null);
  const [err, setErr] = useState("");
  const t = useRef();
  useEffect(() => {
    clearTimeout(t.current);
    if (!query) {
      setLoc(null);
      return;
    }
    t.current = setTimeout(async () => {
      try {
        const d = await fetchRates(query);
        setLoc(d.location);
        setModel && setModel(d.laborModel || DEFAULT_MODEL);
        setErr("");
      } catch (e) {
        setLoc(null);
        setErr(e?.response?.data?.detail || "Not found");
      }
    }, 350);
    return () => clearTimeout(t.current);
  }, [query, setModel]);
  return [loc, err];
}

const LocInput = ({ label, value, onChange, loc, err, testid }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="City or zipcode"
        className="h-11 rounded-sm border-slate-300 bg-white pl-9 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
        data-testid={testid}
      />
    </div>
    {err ? (
      <span className="text-xs font-medium text-red-500">{err}</span>
    ) : loc ? (
      <span className="text-xs text-slate-400">
        <span className="font-semibold text-slate-600">{loc.label}</span>
        {!loc.matched && " (regional estimate)"}
      </span>
    ) : null}
  </div>
);

const Column = ({ loc, bd, winner, testid }) => (
  <div
    className={`overflow-hidden rounded-sm border bg-white ${winner ? "border-green-500 shadow-md" : "border-slate-200"}`}
    data-testid={testid}
  >
    <div className={`px-5 py-4 ${winner ? "bg-green-600" : "bg-slate-900"} text-white`}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/70">
        <MapPin className="h-3.5 w-3.5" /> {loc ? loc.label : "—"}
      </div>
      <div className="mt-1 font-heading text-2xl font-extrabold tracking-tight">
        {loc ? usd(bd.total) : "—"}
      </div>
      {winner && (
        <div className="mt-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide">
          <TrendingDown className="h-3.5 w-3.5" /> Lower cost
        </div>
      )}
    </div>
    {loc && (
      <div className="divide-y divide-slate-100 px-5 py-3">
        {[
          ["Storage", bd.storage],
          ["Inbound", bd.inbound],
          ["Handling & picking", bd.pickLabor],
          ["Repacking", bd.repackLabor],
          ["Outbound", bd.outbound],
          ["G&A / PM", bd.ga],
        ].map(([l, v]) => (
          <div key={l} className="flex items-center justify-between py-2 text-sm">
            <span className="text-slate-600">{l}</span>
            <span className="font-mono-plex font-medium text-slate-900">{usd(v)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default function Compare() {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState(DEFAULTS);
  const [qA, setQA] = useState("New York, NY");
  const [qB, setQB] = useState("Dallas, TX");
  const [model, setModel] = useState(DEFAULT_MODEL);
  const set = (k) => (v) => setInputs((p) => ({ ...p, [k]: v }));

  const [locA, errA] = useLocationRates(qA, setModel);
  const [locB, errB] = useLocationRates(qB, setModel);

  useEffect(() => {
    document.title = "GloGrid VMI Quoter · Compare";
  }, []);

  const bdA = useMemo(() => (locA ? computeQuote(inputs, locA.rates, model) : {}), [inputs, locA, model]);
  const bdB = useMemo(() => (locB ? computeQuote(inputs, locB.rates, model) : {}), [inputs, locB, model]);

  const both = locA && locB;
  const diff = both ? bdB.total - bdA.total : 0;

  return (
    <div className="min-h-screen bg-slate-100">
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-10 md:px-8">
        <button
          onClick={() => navigate("/")}
          className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
          data-testid="compare-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back to calculator
        </button>

        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Compare two locations</h1>
        <p className="mt-2 text-slate-500">Same volumes, two markets. See how regional labor and storage rates change your monthly cost.</p>

        <div className="mt-8 grid gap-8 lg:grid-cols-12">
          {/* Shared inputs */}
          <div className="lg:col-span-4">
            <div className="rounded-sm border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.15em] text-slate-700">Shared Volumes</h3>
              <div className="grid grid-cols-2 gap-4">
                <NumberField id="c-sqft" label="Storage" value={inputs.storageSqft} onChange={set("storageSqft")} suffix="sqft" step={50} testid="c-sqft" />
                <NumberField id="c-months" label="Months" value={inputs.storageMonths} onChange={set("storageMonths")} suffix="mo" min={1} testid="c-months" />
                <NumberField id="c-in" label="Inbound" value={inputs.inboundPallets} onChange={set("inboundPallets")} suffix="plt" testid="c-inbound" />
                <NumberField id="c-out" label="Outbound" value={inputs.outboundPallets} onChange={set("outboundPallets")} suffix="plt" testid="c-outbound" />
                <NumberField id="c-ord" label="Orders" value={inputs.monthlyOrders} onChange={set("monthlyOrders")} suffix="/mo" step={10} testid="c-orders" />
                <NumberField id="c-lines" label="Lines/order" value={inputs.linesPerOrder} onChange={set("linesPerOrder")} suffix="ln" min={1} testid="c-lines" />
                <NumberField id="c-repack" label="Repack" value={inputs.repackUnits} onChange={set("repackUnits")} suffix="units" step={10} testid="c-repack" />
                <NumberField id="c-ga" label="G&A" value={inputs.gaPercent} onChange={set("gaPercent")} suffix="%" step={0.5} testid="c-ga" />
              </div>
            </div>
          </div>

          {/* Comparison */}
          <div className="lg:col-span-8">
            <div className="grid gap-4 sm:grid-cols-2">
              <LocInput label="Location A" value={qA} onChange={setQA} loc={locA} err={errA} testid="compare-input-a" />
              <LocInput label="Location B" value={qB} onChange={setQB} loc={locB} err={errB} testid="compare-input-b" />
            </div>

            {both && (
              <div className="mt-4 rounded-sm border border-slate-200 bg-white px-5 py-3 text-sm text-slate-600" data-testid="compare-delta">
                {diff === 0 ? (
                  <>Both locations cost the same for these volumes.</>
                ) : (
                  <>
                    <span className="font-semibold text-slate-900">{(diff < 0 ? locB : locA).label}</span> is{" "}
                    <span className="font-semibold text-green-600">{usd(Math.abs(diff))}/mo cheaper</span> (
                    {Math.abs((diff / (diff < 0 ? bdA.total : bdB.total)) * 100).toFixed(1)}%) than{" "}
                    <span className="font-semibold text-slate-900">{(diff < 0 ? locA : locB).label}</span>.
                  </>
                )}
              </div>
            )}

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Column loc={locA} bd={bdA} winner={both && bdA.total < bdB.total} testid="compare-col-a" />
              <Column loc={locB} bd={bdB} winner={both && bdB.total < bdA.total} testid="compare-col-b" />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
